import vue from 'vue';
import VueI18n from 'vue-i18n';
import enLocale from '../locale/en.json';
import twLocale from '../locale/tw.json';
import zhHant from 'vuetify/es5/locale/zh-Hant';
import en from 'vuetify/es5/locale/en';
import color_palette from '../locale/color_palette.json';
import {field_prefix, field_suffix, fields} from '@/utils/metrics_definition';
import menu_definitions from '../locale/menu_definitions.json';

class VueI18nEx extends VueI18n{

  /**
   * 翻譯欄位名稱（支援 prefix 和 suffix）
   * @param {string} p_field - 欄位名稱，可能包含 prefix 和 suffix（如 "|total|pupd_dpst_amt|ratio"）
   * @returns {string} 翻譯後的欄位名稱
   */
  tfd(p_field){
    const prefix_list = field_prefix;
    const suffix_list = field_suffix;

    let prefix_text = '';
    for(const em_i in prefix_list){
      if(!p_field.includes(em_i))
        continue;

      p_field = p_field.replace(em_i, '');
      const current_locale = this.locale;
      prefix_text = prefix_text + (prefix_list[em_i][current_locale] || prefix_list[em_i]['en']);
    }

    let suffix_text = '';
    for(const em_i in suffix_list){
      if(!p_field.includes(em_i))
        continue;

      p_field = p_field.replace(em_i, '');
      const current_locale = this.locale;
      suffix_text = suffix_text + (suffix_list[em_i][current_locale] || suffix_list[em_i]['en']);
    }

    // 從 metrics_definition 獲取欄位名稱
    const base_field = this._extract_base_field(p_field);
    let middle_text = '';
    if (base_field && fields[base_field] && fields[base_field].name) {
      const current_locale = this.locale;
      middle_text = fields[base_field].name[current_locale] ||
                    fields[base_field].name['en'] ||
                    base_field;
    } else if (p_field.length > 0) {
      // 如果在 field_definitions 中找不到，回退到舊的 locale 系統
      middle_text = this.te(`fields.${p_field}`) ? this.t(`fields.${p_field}`) : p_field;
    }

    return prefix_text + middle_text + suffix_text;
  }

  /**
   * 取得欄位的描述文字（用於 tooltip）
   * @param {string} p_field - 欄位名稱
   * @returns {string} 描述文字，如果沒有則返回空字串
   */
  tfd_desc(p_field){
    const base_field = this._extract_base_field(p_field);

    if (base_field && fields[base_field] && fields[base_field].description) {
      const current_locale = this.locale;
      return fields[base_field].description[current_locale] ||
             fields[base_field].description['en'] ||
             '';
    }

    return '';
  }

  /**
   * 取得欄位的情境範例（用於欄位字典、tooltip）
   * @param {string} p_field - 欄位名稱
   * @returns {string} 範例文字，如果沒有則返回空字串
   */
  tfd_example(p_field){
    const base_field = this._extract_base_field(p_field);

    if (base_field && fields[base_field] && fields[base_field].example) {
      const current_locale = this.locale;
      return fields[base_field].example[current_locale] ||
             fields[base_field].example['en'] ||
             '';
    }

    return '';
  }

  /**
   * 檢查欄位是否有情境範例
   * @param {string} p_field - 欄位名稱
   * @returns {boolean} 是否有範例
   */
  has_field_example(p_field){
    if (!p_field) return false;
    const base_field = this._extract_base_field(p_field);

    return !!(base_field && fields[base_field] &&
              fields[base_field].example &&
              (fields[base_field].example.tw ||
               fields[base_field].example.en));
  }

  /**
   * 取得欄位對應的顏色配置
   * @param {string} p_field - 欄位名稱
   * @returns {object|null} 返回包含 key 和 value 的物件，如果沒有顏色配置則返回 null
   * 例如：{ key: 'blue', value: '#00B1F2' }
   */
  get_field_colors(p_field){
    const base_field = this._extract_base_field(p_field);

    if (base_field && fields[base_field] && fields[base_field].color_key) {
      const color_key = fields[base_field].color_key;
      const color_value = color_palette[color_key];

      if (color_value) {
        return {
          key: color_key,
          value: color_value
        };
      }
    }

    return null;
  }

  /**
   * 直接從 color_palette 取得色碼
   * @param {string} color_key - 顏色鍵值（如 'blue', 'teal', 'yellow'）
   * @returns {string|null} 色碼（如 '#00B1F2'），如果找不到則返回 null
   */
  get_color(color_key){
    return color_palette[color_key] || null;
  }

  /**
   * 翻譯選單名稱
   * @param {string} menu_key - 選單 key（如 'sys_provider_site_compare'）
   * @returns {string} 翻譯後的選單名稱
   */
  tmenu(menu_key, also_find_old = true){
    if (menu_definitions.menus[menu_key] && menu_definitions.menus[menu_key].name) {
      const current_locale = this.locale;
      return menu_definitions.menus[menu_key].name[current_locale] ||
             menu_definitions.menus[menu_key].name['en'] ||
             menu_key;
    }

    if (!also_find_old)
      return '';

    // Fallback 到舊的 i18n 系統
    return this.te(`menu.${menu_key}`) ? this.t(`menu.${menu_key}`) : menu_key;
  }

  /**
   * 取得選單的描述文字（用於 tooltip）
   * @param {string} menu_key - 選單 key
   * @returns {string} 描述文字，如果沒有則返回空字串
   */
  tmenu_desc(menu_key){
    if (menu_definitions.menus[menu_key] && menu_definitions.menus[menu_key].description) {
      const current_locale = this.locale;
      return menu_definitions.menus[menu_key].description[current_locale] ||
             menu_definitions.menus[menu_key].description['en'] ||
             '';
    }

    return '';
  }

  /**
   * 檢查選單是否有描述（決定是否顯示 tooltip icon）
   * @param {string} menu_key - 選單 key
   * @returns {boolean} 是否有描述
   */
  has_menu_desc(menu_key){
    if (!menu_key) return false;

    return !!(menu_definitions.menus[menu_key] &&
              menu_definitions.menus[menu_key].description &&
              (menu_definitions.menus[menu_key].description.tw ||
               menu_definitions.menus[menu_key].description.en));
  }

  /**
   * 私有方法：提取基礎欄位名稱（移除所有 prefix 和 suffix）
   * @param {string} p_field - 原始欄位名稱
   * @returns {string} 基礎欄位名稱
   * @private
   */
  _extract_base_field(p_field){
    if (!p_field) return '';

    let base_field = p_field;
    const prefix_list = field_prefix;
    const suffix_list = field_suffix;

    // 移除所有 prefix
    for(const prefix in prefix_list){
      base_field = base_field.replace(prefix, '');
    }

    // 移除所有 suffix
    for(const suffix in suffix_list){
      base_field = base_field.replace(suffix, '');
    }

    return base_field;
  }
}

vue.use(VueI18nEx);

const messages = {
  en: {
    ...enLocale,
    ...en,
  },
  tw: {
    ...twLocale,
    ...zhHant,
  },
};

const language = localStorage.getItem('language') ? localStorage.getItem('language') : 'en';
// Create VueI18nEx instance with options
let i18n = new VueI18nEx({
  locale: language, // set locale
  messages, // set locale messages
  silentTranslationWarn: true,
});

export default i18n;
